import React, { useState, useEffect, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Paper,
  Grid,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Drawer,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import {
  fetchAllChannelInventories,
  createChannelInventory,
  updateChannelInventory,
  deleteChannelInventory,
} from '../../services/channelInventoryService';
import { fetchAllChannels } from '../../services/channelService';
import { getConfigurationRoomTypes } from '../../services/api';
import { getRoomTypeInventory } from '../../services/roomInventoryService';
import {
  getDistributionSectionPaperSx,
  getDistributionSectionHeaderRowSx,
  getDistributionSectionHeaderLeadSx,
  getDistributionSectionIconBoxSx,
  getDistributionSectionIconFontSize,
  getDistributionSectionTitleSx,
  getDistributionSectionSubtitleSx,
  getDistributionActionButtonSx,
  isDistributionMobileLayout,
} from '../../utils/distributionMobileLayout';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getGuestDrawerContentSx,
  getGuestDrawerFieldSx,
  getGuestDrawerSelectSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  BOOKING_DRAWER_ACCENT,
} from '../../utils/bookingDrawerLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import ChannelInventoryCard from './ChannelInventoryCard';
import { THEME_COLORS } from '../../shared/constants/theme';

const THEME_COLOR = THEME_COLORS.PRIMARY;

const ChannelInventorySection = () => {
  const theme = useTheme();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const isMobileLayout = isDistributionMobileLayout();
  const drawerTheme = getBookingDrawerTheme(isDark);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface]
  );

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);
  const [inventories, setInventories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingInventory, setEditingInventory] = useState(null);
  const [inventoryToDelete, setInventoryToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [maxRooms, setMaxRooms] = useState(0);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    channel_id: '',
    room_type_id: '',
    allocated_rooms: 0,
    is_active: true,
  });
  const [errors, setErrors] = useState({});

  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data || {};
    if (data.error) return data.error;
    if (data.message) return data.message;
    if (Array.isArray(data.errors?.__all__) && data.errors.__all__.length) {
      return data.errors.__all__.join(', ');
    }
    return fallback;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inventoriesData, channelsData, roomTypesData] = await Promise.all([
        fetchAllChannelInventories(),
        fetchAllChannels(),
        getConfigurationRoomTypes(),
      ]);
      setInventories(inventoriesData.inventories || []);
      setChannels(channelsData.channels || []);
      setRoomTypes(roomTypesData || []);
    } catch (error) {setSnackbar({
        open: true,
        message: 'Failed to load data. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDrawer = (inventory = null) => {
    if (inventory) {
      setEditingInventory(inventory);
      setFormData({
        channel_id: inventory.channel_id || '',
        room_type_id: inventory.room_type_id || '',
        allocated_rooms: inventory.allocated_rooms || 0,
        is_active: inventory.is_active !== undefined ? inventory.is_active : true,
      });
      // Fetch max rooms for existing inventory
      if (inventory.room_type_id) {
        fetchMaxRooms(inventory.room_type_id, false);
      }
    } else {
      setEditingInventory(null);
      setFormData({
        channel_id: '',
        room_type_id: '',
        allocated_rooms: 0,
        is_active: true,
      });
      setMaxRooms(0);
    }
    setErrors({});
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingInventory(null);
    setMaxRooms(0);
    setErrors({});
  };

  const fetchMaxRooms = async (roomTypeId, shouldAutoFill = false) => {
    if (!roomTypeId) {
      setMaxRooms(0);
      return;
    }

    setLoadingInventory(true);
    try {
      const data = await getRoomTypeInventory(roomTypeId);
      const total = data.total_rooms || 0;
      setMaxRooms(total);
      
      // Auto-fill allocated_rooms with total if shouldAutoFill is true
      if (shouldAutoFill && total > 0) {
        setFormData(prev => ({ ...prev, allocated_rooms: total }));
      }
    } catch (error) {setMaxRooms(0);
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleInputChange = async (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // When room type changes, fetch max rooms and auto-fill
    if (field === 'room_type_id' && value) {
      await fetchMaxRooms(value, !editingInventory); // Auto-fill only when adding new inventory
    }
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.channel_id) {
      newErrors.channel_id = 'Channel is required';
    }
    
    if (!formData.room_type_id) {
      newErrors.room_type_id = 'Room type is required';
    }
    
    if (formData.allocated_rooms < 0) {
      newErrors.allocated_rooms = 'Allocated rooms cannot be negative';
    }
    
    if (maxRooms > 0 && formData.allocated_rooms > maxRooms) {
      newErrors.allocated_rooms = `Allocated rooms cannot exceed total inventory (${maxRooms} rooms)`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (editingInventory) {
        await updateChannelInventory(editingInventory.id, formData);
        setSnackbar({
          open: true,
          message: 'Channel inventory updated successfully',
          severity: 'success',
        });
      } else {
        await createChannelInventory(formData);
        setSnackbar({
          open: true,
          message: 'Channel inventory created successfully',
          severity: 'success',
        });
      }
      handleCloseDrawer();
      fetchData();
    } catch (error) {if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        const allErrors = error.response.data.errors?.__all__;
        if (Array.isArray(allErrors) && allErrors.length) {
          setSnackbar({
            open: true,
            message: allErrors.join(', '),
            severity: 'error',
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: getApiErrorMessage(error, 'Failed to save channel inventory. Please try again.'),
          severity: 'error',
        });
      }
    }
  };

  const handleDeleteClick = (inventory) => {
    setInventoryToDelete(inventory);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!inventoryToDelete) return;

    setDeletingId(inventoryToDelete.id);
    try {
      await deleteChannelInventory(inventoryToDelete.id);
      setSnackbar({
        open: true,
        message: 'Channel inventory deleted permanently',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setInventoryToDelete(null);
      fetchData();
    } catch (error) {setSnackbar({
        open: true,
        message: getApiErrorMessage(
          error,
          'Failed to delete channel inventory. Remove related mapping first and try again.'
        ),
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Group inventories by channel
  const groupedInventories = inventories.reduce((acc, inventory) => {
    const channelName = inventory.channel_name || 'Unknown';
    if (!acc[channelName]) {
      acc[channelName] = {
        channel: {
          id: inventory.channel_id,
          name: inventory.channel_name,
          type: inventory.channel_type,
          type_display: inventory.channel_type_display,
          image_path: inventory.channel_image_path || '',
        },
        inventories: [],
      };
    }
    acc[channelName].inventories.push(inventory);
    return acc;
  }, {});

  return (
    <Box>
      <Paper
        elevation={0}
        sx={getDistributionSectionPaperSx(theme, isMobileLayout)}
      >
        <Box sx={getDistributionSectionHeaderRowSx(isMobileLayout)}>
          <Box sx={getDistributionSectionHeaderLeadSx(isMobileLayout)}>
            <Box sx={getDistributionSectionIconBoxSx(THEME_COLOR, isMobileLayout)}>
              <InventoryIcon sx={{ fontSize: getDistributionSectionIconFontSize(isMobileLayout), color: THEME_COLOR }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={getDistributionSectionTitleSx(isMobileLayout)}>
                Channel Inventory
              </Typography>
              <Typography variant="body2" sx={getDistributionSectionSubtitleSx(isMobileLayout)}>
                Allocate room inventory across your distribution channels
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDrawer()}
            sx={getDistributionActionButtonSx(THEME_COLOR, isMobileLayout)}
          >
            Add Inventory
          </Button>
        </Box>

        {/* Inventory Cards */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : inventories.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            No inventory allocations found. Click "Add Inventory" to create one.
          </Typography>
        ) : (
          <Grid container spacing={2.5} sx={{ '& .MuiGrid-item': { display: 'flex' } }}>
            {Object.values(groupedInventories).map((group) => (
              <Grid item xs={12} sm={6} lg={4} key={group.channel.id || group.channel.name} sx={{ display: 'flex' }}>
                <ChannelInventoryCard
                  group={group}
                  isDark={isDark}
                  onEdit={handleOpenDrawer}
                  onDelete={handleDeleteClick}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Add/Edit Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, {
            width: { xs: '100%', sm: 600 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }),
        }}
      >
        <Box
          sx={{
            ...getBookingDrawerHeaderSx(isDark),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box sx={getDistributionSectionIconBoxSx(THEME_COLOR, isMobileLayout)}>
              <InventoryIcon sx={{ fontSize: 22, color: THEME_COLOR }} />
            </Box>
            <Typography variant="h6" sx={{ color: drawerTheme.text, fontWeight: 700, fontSize: '1.05rem' }}>
              {editingInventory ? 'Edit Inventory Allocation' : 'Add Inventory Allocation'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDrawer}
            size="small"
            sx={{
              color: drawerTheme.subtleText,
              '&:hover': { backgroundColor: isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.04) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            ...getGuestDrawerContentSx(isDark),
          }}
        >
            <FormControl fullWidth error={!!errors.channel_id} required>
              <InputLabel>Channel</InputLabel>
              <Select
                value={formData.channel_id}
                onChange={(e) => handleInputChange('channel_id', e.target.value)}
                label="Channel"
                disabled={!!editingInventory}
                sx={getGuestDrawerSelectSx(isDark, !!errors.channel_id)}
                MenuProps={drawerOverlays.selectMenuProps}
              >
                {channels.map((channel) => (
                  <MenuItem key={channel.id} value={channel.id}>
                    {channel.name} ({channel.channel_type_display || channel.channel_type})
                  </MenuItem>
                ))}
              </Select>
              {errors.channel_id && <FormHelperText>{errors.channel_id}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth error={!!errors.room_type_id} required>
              <InputLabel>Room Type</InputLabel>
              <Select
                value={formData.room_type_id}
                onChange={(e) => handleInputChange('room_type_id', e.target.value)}
                label="Room Type"
                disabled={!!editingInventory}
                sx={getGuestDrawerSelectSx(isDark, !!errors.room_type_id)}
                MenuProps={drawerOverlays.selectMenuProps}
              >
                {roomTypes.map((roomType) => (
                  <MenuItem key={roomType.id} value={roomType.id}>
                    {roomType.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.room_type_id && <FormHelperText>{errors.room_type_id}</FormHelperText>}
            </FormControl>

            {loadingInventory ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: THEME_COLOR }} />
                <Typography variant="body2" sx={{ color: drawerTheme.subtleText }}>
                  Loading inventory...
                </Typography>
              </Box>
            ) : maxRooms > 0 && (
              <Typography variant="body2" sx={{ color: drawerTheme.subtleText, mb: -1 }}>
                Total inventory: {maxRooms} rooms
              </Typography>
            )}

            <TextField
              label="Allocated Rooms"
              type="number"
              value={formData.allocated_rooms}
              onChange={(e) => handleInputChange('allocated_rooms', parseInt(e.target.value, 10) || 0)}
              error={!!errors.allocated_rooms}
              helperText={errors.allocated_rooms || (maxRooms > 0 ? `Maximum: ${maxRooms} rooms (0 = sold out)` : 'Number of rooms allocated to this channel (0 = sold out)')}
              fullWidth
              inputProps={{ min: 0, max: maxRooms > 0 ? maxRooms : undefined }}
              sx={getGuestDrawerFieldSx(isDark, !!errors.allocated_rooms)}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: BOOKING_DRAWER_ACCENT },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: alpha(BOOKING_DRAWER_ACCENT, 0.45),
                    },
                  }}
                />
              }
              label="Active"
              sx={{ color: drawerTheme.text, ml: 0.25 }}
            />
        </Box>

        <Box sx={{ ...getBookingDrawerFooterSx(isDark), p: 1.5, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="outlined" onClick={handleCloseDrawer} fullWidth sx={getBookingDrawerOutlinedButtonSx(isDark)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} fullWidth sx={getBookingDrawerPrimaryButtonSx(isDark)}>
              {editingInventory ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Inventory Allocation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the inventory allocation for "{inventoryToDelete?.channel_name}" - "{inventoryToDelete?.room_type_name}"? This action cannot be undone.
            If this channel-room type has active mappings, delete those mappings first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deletingId === inventoryToDelete?.id}
          >
            {deletingId === inventoryToDelete?.id ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChannelInventorySection;

